import { useCallback, useState } from 'react';
import type { Exercise } from '@heraion/shared';

import type { DurableWriteFn, NotifyFn } from '../seams';
import {
  defaultRestSecFor,
  REST_FALLBACK_SEC,
  REST_PICKER_MIN,
  REST_PICKER_MAX,
} from '@heraion/shared';

// Owns the in-Logger rest-default picker (1.82.0 slider Modal). Extracted
// verbatim out of LoggerScreen (Track R) so the screen just wires the
// returned state + handlers into the rest banner button and
// RestDefaultPicker. State lives here (not in the banner) so the Modal
// can render outside the banner's conditional-on-running gate — the
// timer can finish mid-picker and we don't want the picker to vanish
// under the user's finger.
type UseRestPickerArgs = {
  catalog: Exercise[] | null;
  setCatalog: React.Dispatch<React.SetStateAction<Exercise[] | null>>;
  /** Profile.defaultRestSec, used as the seed fallback for exercises
   *  that predate any per-exercise tuning. */
  profileDefaultRestSec: number | null | undefined;
  /** Re-pull the profile after a global-default save. */
  refreshProfile: () => Promise<unknown> | void;
  /** The exercise the running rest timer is counting for; the picker
   *  opens against it. Null when no timer is active. */
  activeRestExerciseId: string | null;
  t: (k: string, opts?: Record<string, unknown>) => string;
  durableWrite: DurableWriteFn;
  /** Get-merge-PATCH the host's Profile (mobile: patchProfile; web-rnw:
   *  its own Profile-doc write). */
  patchProfile: (patch: { defaultRestSec: number }) => Promise<void>;
  notify: NotifyFn;
  /** Marks the host's catalog source stale (#342). */
  onCatalogSaved?: () => void;
};

export function useRestPicker({
  catalog,
  setCatalog,
  profileDefaultRestSec,
  refreshProfile,
  activeRestExerciseId,
  t,
  durableWrite,
  patchProfile,
  notify,
  onCatalogSaved,
}: UseRestPickerArgs) {
  const [savingDefaultRest, setSavingDefaultRest] = useState(false);
  const [restPickerOpen, setRestPickerOpen] = useState(false);
  const [restPickerSlug, setRestPickerSlug] = useState<string | null>(null);
  const [restPickerValue, setRestPickerValue] =
    useState<number>(REST_FALLBACK_SEC);
  // Inbox ddac68ab: when checked, the modal saves to Profile.defaultRestSec
  // instead of Exercise.defaultRestSec. Default off so the existing
  // per-exercise flow stays one tap. Reset every time the modal closes
  // so a previous "global" pick doesn't accidentally carry over.
  const [restPickerSaveAsGlobal, setRestPickerSaveAsGlobal] =
    useState<boolean>(false);

  const applyDefaultRest = useCallback(
    async (slug: string, seconds: number) => {
      setSavingDefaultRest(true);
      try {
        // Stock catalog rows live in CATALOG_PK; PATCHing them needs
        // `?tier=stock` so the API targets the global row instead of
        // creating a user-scoped clone under USER#<sub>. Mirrors the
        // legacy web editor (7ae8e35e:apps/web/src/exercises/ExerciseCatalog.tsx:450):
        // without this, the request 404s on the user partition for
        // any stock exercise (the bulk of the catalog). User-source
        // rows are partition-resolved by the caller's sub so no
        // query param is needed.
        const ex = catalog?.find((x) => x.slug === slug);
        const tierQuery = ex?.source === 'stock' ? '?tier=stock' : '';
        // A queued PATCH keeps the local catalog edit and returns true: the
        // write is durable, so blocking the athlete's rest default behind
        // the network would be the DX-2 bug in miniature.
        const result = await durableWrite({
          method: 'PATCH',
          path: `/api/exercises/${encodeURIComponent(slug)}${tierQuery}`,
          body: { defaultRestSec: seconds },
        });
        if (result.outcome === 'rejected') throw result.error;
        setCatalog((prev) =>
          prev
            ? prev.map((ex) =>
                ex.slug === slug ? { ...ex, defaultRestSec: seconds } : ex,
              )
            : null,
        );
        onCatalogSaved?.();
        return true;
      } catch {
        notify(
          t('mobile.logger.restEditTitle'),
          t('mobile.logger.restEditError'),
        );
        return false;
      } finally {
        setSavingDefaultRest(false);
      }
    },
    [catalog, setCatalog, t, durableWrite, onCatalogSaved, notify],
  );
  // Sibling of applyDefaultRest that targets Profile.defaultRestSec
  // instead of Exercise.defaultRestSec. Used by the in-Logger slider
  // modal when the "Use as global default" checkbox is on. The full-doc
  // round-trip it used to spell out by hand now lives in patchProfile —
  // the mobile snapshot is still too narrow to rebuild a PUT body locally,
  // which is exactly why that round-trip has one home instead of eight.
  const applyGlobalDefaultRest = useCallback(
    async (seconds: number): Promise<boolean> => {
      setSavingDefaultRest(true);
      try {
        await patchProfile({ defaultRestSec: seconds });
        await refreshProfile();
        return true;
      } catch {
        notify(
          t('mobile.logger.restEditTitle'),
          t('mobile.logger.restEditError'),
        );
        return false;
      } finally {
        setSavingDefaultRest(false);
      }
    },
    [refreshProfile, t, patchProfile, notify],
  );

  const open = useCallback(() => {
    const slug = activeRestExerciseId;
    if (!slug) return;
    const ex = catalog?.find((x) => x.slug === slug);
    setRestPickerSlug(slug);
    // Seed from the catalog's stored default; fall back to the profile
    // global default when the row predates any per-exercise tuning.
    // Clamp into the slider domain (REST_PICKER_MIN..REST_PICKER_MAX)
    // so the thumb never starts off-track on legacy outliers.
    const seed = defaultRestSecFor(ex?.defaultRestSec, profileDefaultRestSec);
    setRestPickerValue(
      Math.max(REST_PICKER_MIN, Math.min(REST_PICKER_MAX, seed)),
    );
    setRestPickerOpen(true);
  }, [activeRestExerciseId, catalog, profileDefaultRestSec]);

  const cancel = useCallback(() => {
    setRestPickerOpen(false);
    setRestPickerSaveAsGlobal(false);
  }, []);

  const toggleSaveAsGlobal = useCallback(() => {
    setRestPickerSaveAsGlobal((v) => !v);
  }, []);

  const confirm = useCallback(() => {
    void (async () => {
      const ok = restPickerSaveAsGlobal
        ? await applyGlobalDefaultRest(restPickerValue)
        : restPickerSlug
          ? await applyDefaultRest(restPickerSlug, restPickerValue)
          : false;
      if (ok) {
        setRestPickerOpen(false);
        setRestPickerSaveAsGlobal(false);
      }
    })();
  }, [
    restPickerSaveAsGlobal,
    restPickerValue,
    restPickerSlug,
    applyGlobalDefaultRest,
    applyDefaultRest,
  ]);

  return {
    saving: savingDefaultRest,
    visible: restPickerOpen,
    slug: restPickerSlug,
    value: restPickerValue,
    saveAsGlobal: restPickerSaveAsGlobal,
    setValue: setRestPickerValue,
    open,
    cancel,
    toggleSaveAsGlobal,
    confirm,
  };
}
